/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { DownloadOutlined } from '@ant-design/icons';
import { OrderDoc, OrderStatusCode } from '@gooduncles/gu-app-schema';
import { Button } from 'antd';
import { FC } from 'react';
import { formatNumber } from 'src/utils/common';
import { calculateIssueTasksAmount } from 'src/utils/issue-task-util';
import { getHistoryItems, historyExcelDownload } from 'src/utils/order-history-util';
import { WebViewMessageHandler } from 'src/utils/webview/webveiw-message-handler';
import { v4 } from 'uuid';

import { selectStore } from 'src/redux/slices/auth';
import { selectOrdersForHistory } from 'src/redux/slices/order';
import { useAppSelector } from 'src/redux/store';

import useIssueTasksForStore from 'src/hooks/useIssueTasksForStore';
import useStoreIssues from 'src/hooks/useStoreIssues';

import EmptyList from 'src/components/atoms/EmptyList/EmptyList';
import TopNavigationBar from 'src/components/atoms/TopNavigationBar/TopNavigationBar';
import SendOrderHistoryBar from 'src/components/molecules/SendOrderHistoryBar/SendOrderHistoryBar';

import classes from './OrderHistory.module.scss';
import OrderHistoryItem from './OrderHistoryItem/OrderHistoryItem';
import StoreIssueGroup from './StoreIssueGroup/StoreIssueGroup';

const webViewMessageHandler = WebViewMessageHandler.getInstance();

const OrderHistory: FC = () => {
  const store = useAppSelector(selectStore);
  const orders = useAppSelector(selectOrdersForHistory);
  const { storeIssues } = useStoreIssues(store?.storeCode ?? null);
  const { issueTasks } = useIssueTasksForStore(store?._id ?? null);
  const historyItems = getHistoryItems(orders ?? [], storeIssues, issueTasks);

  return (
    <div className={classes.orderHistoryContainer}>
      <TopNavigationBar title='주문 히스토리' leftButton />
      <SendOrderHistoryBar />
      {Object.keys(historyItems)?.length > 0 ? (
        <div className={classes.groupList}>
          <p className={classes.inform}>총 금액은 정산 과정에서 변동될 수 있습니다.</p>
          {Object.entries(historyItems).map(([groupDate, items]) => {
            const issueTasks = items
              .filter((item) => item?._ui?.issueTasks && item._ui.issueTasks.length > 0)
              .flatMap((item) => item._ui!.issueTasks);
            const issues = items
              .filter((item) => item?._ui?.storeIssues && item._ui.storeIssues.length > 0)
              .flatMap((item) => item._ui!.storeIssues);
            const totalIssueAmount =
              issues.reduce((acc, cur) => acc + ((cur.supplyPrice ?? 0) + (cur.tax ?? 0)) * (cur.volume ?? 1), 0) ?? 0;
            const totalIssueTaskAmount = calculateIssueTasksAmount(issueTasks);
            const sumOfGrandTotal = items
              .filter(
                (i) =>
                  i.grandTotal &&
                  i.orderStatus &&
                  [OrderStatusCode.ACCEPTED, OrderStatusCode.DELIVERED].includes(i.orderStatus)
              )
              .reduce((acc, cur) => acc + cur.grandTotal!, 0);

            return (
              <div className={classes.group} key={groupDate}>
                <div className={classes.border} />
                <div className={classes.summary}>
                  <div className={classes.topRow}>
                    <h3>
                      {groupDate} <span>- {formatNumber(items.length)}건</span>
                    </h3>
                    {/* 현재는 웹에서만 다운로드가 가능하다 */}
                    {webViewMessageHandler.getWebViewRef() ? null : (
                      <Button icon={<DownloadOutlined />} onClick={() => historyExcelDownload(groupDate, items)} />
                    )}
                  </div>
                  <div className={classes.info}>
                    <h2>{formatNumber(sumOfGrandTotal + totalIssueAmount + totalIssueTaskAmount)}</h2>
                    <p>원</p>
                  </div>
                </div>
                <div className={classes.orderList}>
                  {items.map((order) =>
                    // 주문의 여부에 따라 주문(+이슈) 또는 Only 이슈로 분기한다.
                    order.orderStatus ? (
                      <OrderHistoryItem key={order._id} order={order as OrderDoc} />
                    ) : (
                      <StoreIssueGroup
                        key={v4()}
                        orderDate={order.orderDate}
                        storeIssues={order._ui?.storeIssues ?? []}
                        issueTasks={order._ui?.issueTasks ?? []}
                      />
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyList emoji='🧾' message='이전 주문 내역이 없습니다.' />
      )}
    </div>
  );
};

export default OrderHistory;
